import {
  DownloadIcon,
  SparklesIcon,
  UploadIcon,
} from '@heroicons/react/outline';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import { ImgComparisonSlider } from '@img-comparison-slider/react';
import { ConfigProvider, Slider } from 'antd';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';
import uuidv4 from '@/lib/uuid';

import { useAnalytics } from '@/contexts/Analytics';
import axiosInstance from '@/utils/httpClient';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';

import Select from '../atoms/Select';
import Button from '../buttons/Button';
import ButtonLink from '../links/ButtonLink';
import Spinner from '../Spinner';

import { SimpleTooltip } from '@/components/ui/tooltip';

export const Upscaler = ({
  beforeImg = '',
  afterImg = '',
  onUpscale,
}: {
  beforeImg?: string;
  afterImg?: string;
  onUpscale?: (img: string) => void;
}) => {
  const supabaseClient = createClient();
  const { t } = useLocale('ai-images');

  const [uploadedFiles, setUploadedFiles] = useState<any>({});
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [newBeforeImage, setNewBeforeImage] = useState(beforeImg);
  const [newAfterImage, setNewAfterImage] = useState(afterImg);
  const [creativity, setCreativity] = useState(3);
  const [HDR, setHDR] = useState(6);
  const [resemblance, setResemblance] = useState(6);
  const [fractality, setFractality] = useState(6);
  const [scaleFactor, setScaleFactor] = useState(2);
  const [mode, setMode] = useState('Crisp');

  const analytics = useAnalytics();
  const { isFreePlan } = useUser();

  const beforeImage = beforeImg;
  const afterImage = afterImg;

  const handleUpscale = async () => {
    setIsFileLoading(true);
    try {
      const response = await axiosInstance.post('/api/magic-upscaler', {
        image: beforeImage || newBeforeImage,
        creativity,
        HDR,
        resemblance,
        scaleFactor,
        fractality,
        mode,
      });

      setNewBeforeImage(beforeImage as string);
      setNewAfterImage(response.data.data.image_url as string);

      onUpscale && onUpscale(response.data.data.image_url as string);

      setIsFileLoading(false);
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.includes(' reached ')
      ) {
        analytics.track('Image limit reached', {
          page: 'Magic Upscaler',
        });
        toast.error(() => (
          <div>
            <div className='mb-5'>
              {t(
                `You have reached the maximum number of generated images. Please upgrade your plan.`,
              )}
            </div>
            <ButtonLink variant='dark' href='/settings/billing'>
              {t('Upgrade plan')}
            </ButtonLink>
          </div>
        ));
      }
      setIsFileLoading(false);
    }
  };

  const handleFileUpload = async (fieldName: string) => {
    // upload to Supabase
    setIsFileLoading(true);
    const file = (document.getElementById(fieldName) as any).files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `upscale/${fileName}`;

    // Add validation for image width
    const image = new Image();
    image.src = URL.createObjectURL(file);
    const isImageLoaded = new Promise((resolve, reject) => {
      image.onload = () => resolve(true);
      image.onerror = () => reject(new Error('Image loading failed'));
    });

    try {
      await isImageLoaded;
      if (image.width > 2000) {
        throw new Error(
          'Image width is more than 1500 pixels. Please upload an image with a width of less than 1500 pixels.',
        );
      }
    } catch (error) {
      alert(error);
      setIsFileLoading(false);
      return;
    }
    const { data, error } = await supabaseClient.storage
      .from('uploads')
      .upload(filePath, file);

    if (error) {
      console.log(error);
      return;
    }

    const fileData = await supabaseClient.storage
      .from('uploads')
      .getPublicUrl(filePath);

    // send to API
    try {
      const response = await axiosInstance.post('/api/magic-upscaler', {
        image: fileData.data.publicUrl,
        creativity,
        HDR,
        resemblance,
        scaleFactor,
      });

      setNewBeforeImage(fileData.data.publicUrl as string);
      setNewAfterImage(response.data.data as string);

      setIsFileLoading(false);
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.includes(' reached ')
      ) {
        analytics.track('Image limit reached', {
          page: 'Magic Upscaler',
        });
        toast.error(() => (
          <div>
            <div className='mb-5'>
              {t(
                `You have reached the maximum number of generated images. Please upgrade your plan.`,
              )}
            </div>
            <ButtonLink variant='dark' href='/settings/billing'>
              {t('Upgrade plan')}
            </ButtonLink>
          </div>
        ));
      }
      setIsFileLoading(false);
    }
  };

  return (
    <main className='mx-auto mt-3 pb-5'>
      <div className=''>
        <div className='relative z-30 text-center md:mx-auto'>
          <h1 className='mt-1  block bg-gradient-to-r from-indigo-400 to-pink-600 bg-clip-text pb-3 text-4xl font-bold tracking-tight text-transparent sm:text-5xl '>
            {t('Magic Upscaler')}
          </h1>
          <p className='text-base text-gray-500  sm:text-xl lg:text-lg'>
            {t(
              'Upscale images with our Magic Upscaler. Try the default settings first and adjust as needed.',
            )}
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-4 sm:px-6 md:grid-cols-5 lg:px-8'>
        <div>
          {/* <h2 className='text-base font-semibold leading-7 text-white'>
            Personal Information
          </h2>
          <p className='mt-1 text-sm leading-6 text-gray-400'>
            Use a permanent address where you can receive mail.
          </p> */}
          <ConfigProvider
            theme={{
              components: {
                Slider: {
                  trackBg: '#a664c4',
                  trackHoverBg: '#8437e9',
                  railSize: 12,
                  dotSize: 24,
                },
              },
              token: {
                // Seed Token
                colorPrimary: 'rgb(99, 102, 241)',
                borderRadius: 2,

                // Alias Token
                // colorBgContainer: '#f6ffed',
              },
            }}
          >
            <div className='mb-6 sm:col-span-6'>
              <div className='flex items-center'>
                <label
                  htmlFor=''
                  className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                >
                  {t('Mode')}{' '}
                  <SimpleTooltip
                    title={
                      <div className='space-y-2'>
                        <p>
                          <strong>{t('Crisp')}:</strong>{' '}
                          {t(
                            'Designed to make images sharper and cleaner, increasing overall quality and making visuals suitable for web use or print-ready materials',
                          )}
                        </p>
                        <p>
                          <strong>{t('Creative')}:</strong>{' '}
                          {t(
                            'Focuses on enhancing details and refining complex elements in the image, adding depth by improving textures, fine details, and features',
                          )}
                        </p>
                        <p>
                          <strong>{t('Restore old photo')}:</strong>{' '}
                          {t(
                            'Uses advanced technology to restore and enhance old photographs',
                          )}
                        </p>
                      </div>
                    }
                  >
                    <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                  </SimpleTooltip>
                </label>
              </div>
              <div className='mt-1 flex flex-col'>
                <Select
                  options={[t('Creative'), t('Crisp'), t('Restore old photo')]}
                  value={mode}
                  label='Mode'
                  onChange={(mode) => setMode(mode)}
                />
              </div>
            </div>
            {mode === 'Creative' && (
              <>
                <div className='sm:col-span-6'>
                  <div className='flex items-center'>
                    <label
                      htmlFor=''
                      className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                    >
                      {t('Creativity')}{' '}
                      <SimpleTooltip
                        title={t(
                          'More freedom to the AI to hallucinate details. If too high it hallucinates too much, try to increase Resemblance. If too low, the output will not improve much from the input.',
                        )}
                      >
                        <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                      </SimpleTooltip>
                    </label>
                  </div>
                  <div className='mt-1 flex flex-col'>
                    <Slider
                      className='rounded'
                      min={0}
                      step={0.1}
                      max={6}
                      value={creativity}
                      onChange={setCreativity}
                    />
                  </div>
                </div>
                <div className='sm:col-span-6'>
                  <div className='flex items-center'>
                    <label
                      htmlFor=''
                      className='flex cursor-help items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                    >
                      {t('HDR')}
                      <SimpleTooltip
                        title={t(
                          'Increase to get stronger details. If too high, the image may look pixelated.',
                        )}
                      >
                        <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                      </SimpleTooltip>
                    </label>
                  </div>
                  <div className='mt-1 flex flex-col'>
                    <Slider
                      className=''
                      min={1}
                      step={1}
                      max={11}
                      value={HDR}
                      onChange={setHDR}
                    />
                  </div>
                </div>
                <div className='sm:col-span-6'>
                  <div className='flex items-center'>
                    <label
                      htmlFor=''
                      className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                    >
                      {t('Resemblance')}
                      <SimpleTooltip
                        title={t(
                          'Increase to stick closer to the original image.',
                        )}
                      >
                        <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                      </SimpleTooltip>
                    </label>
                  </div>
                  <div className='mt-1 flex flex-col'>
                    <Slider
                      className=''
                      min={0}
                      step={0.1}
                      max={12}
                      value={resemblance}
                      onChange={setResemblance}
                    />
                  </div>
                </div>
                <div className='sm:col-span-6'>
                  <div className='flex items-center'>
                    <label
                      htmlFor=''
                      className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                    >
                      {t('Fractality')}
                      <SimpleTooltip
                        title={t('Increase to get more small details.')}
                      >
                        <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                      </SimpleTooltip>
                      {isFreePlan && (
                        <Link
                          href='/settings/billing'
                          className='ml-1 text-xs text-primary-500 hover:underline'
                        >
                          {t('Upgrade')}
                        </Link>
                      )}
                    </label>
                  </div>
                  <div className='mt-1 flex flex-col'>
                    <Slider
                      className=''
                      min={1}
                      step={1}
                      max={12}
                      disabled={isFreePlan}
                      value={fractality}
                      onChange={setFractality}
                    />
                  </div>
                </div>{' '}
              </>
            )}
            <div className='sm:col-span-6'>
              <div className='flex items-center'>
                <label
                  htmlFor=''
                  className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'
                >
                  {t('Scale Factor')}
                  <SimpleTooltip
                    title={t('Increase to get a higher resolution image.')}
                  >
                    <QuestionMarkCircleIcon className='ml-0.5 h-4 w-4 text-gray-500' />
                  </SimpleTooltip>
                  {isFreePlan && (
                    <Link
                      href='/settings/billing'
                      className='ml-1 text-xs text-primary-500 hover:underline'
                    >
                      {t('Upgrade')}
                    </Link>
                  )}
                </label>
              </div>
              <div className='mt-1 flex flex-col'>
                <Select
                  options={['2', '4']}
                  disabledOptions={isFreePlan ? ['2', '4'] : []}
                  label=''
                  value='scaleFactor'
                  onChange={(e) => setScaleFactor(Number(e))}
                />
              </div>
            </div>
          </ConfigProvider>
          <div className='mt-10 flex flex-col items-center'>
            {uploadedFiles['upscale_image'] && (
              <div className='mb-5 mt-5'>
                <div className='mb-4 flex items-center pr-1 last:mb-0'>
                  <div className='flex items-center'>
                    <span className='text-base'>
                      <img
                        height={150}
                        className='max-h-64 rounded-lg'
                        src={URL.createObjectURL(
                          (document.getElementById('upscale_image') as any)
                            .files[0],
                        )}
                      />
                    </span>
                  </div>
                </div>
              </div>
            )}
            {beforeImg || newBeforeImage ? (
              <>
                {isFreePlan ? (
                  <ButtonLink
                    variant='dark'
                    href='/settings/billing'
                    target='_blank'
                    className='mt-4 flex w-full items-center justify-center'
                  >
                    {t('Upgrade')}
                  </ButtonLink>
                ) : (
                  <Button
                    onClick={handleUpscale}
                    isLoading={isFileLoading}
                    disabled={isFileLoading}
                    variant='dark'
                    className='mt-5 flex w-full items-center justify-center'
                  >
                    <SparklesIcon className='mr-2 h-6 w-6' />
                    <span>
                      {t('Upscale')} ({1 * scaleFactor} {t('credit')})
                    </span>
                  </Button>
                )}
              </>
            ) : (
              <label className='text-blue border-blue mt-1 flex w-64 cursor-pointer items-center rounded-lg border bg-white px-4 py-2 tracking-wide shadow-lg hover:border-primary-500 hover:text-primary-500 dark:bg-slate-800'>
                {isFileLoading ? (
                  <Spinner className='animate-spin-fast mr-4 h-8 w-8 text-primary-500' />
                ) : (
                  <UploadIcon className='mr-4 h-8 w-8 text-primary-500' />
                )}
                {isFileLoading ? (
                  <span className='mt-2 text-center'>
                    {t('Uploading...')}
                    <br />
                    <span className='text-xs text-gray-500'>
                      Can take about 30-40 seconds
                    </span>
                  </span>
                ) : (
                  <span className='mt-2 flex flex-col text-center'>
                    {t('Click to upload a file')}
                    <span className=' text-xs text-gray-500'>
                      jpg, png, webp
                    </span>
                  </span>
                )}

                <input
                  type='file'
                  className='hidden'
                  id='upscale_image'
                  accept='image/*'
                  onChange={() => handleFileUpload('upscale_image')}
                />
              </label>
            )}
          </div>
          {newAfterImage && (
            <div className='mt-5 flex justify-center space-x-4'>
              <Button
                variant='outline'
                className='flex w-full items-center justify-center'
                onClick={() => {
                  const newImage = newAfterImage;
                  const link = document.createElement('a');
                  link.href = newImage;
                  link.target = '_blank';
                  link.download = 'upscaled_image.png';
                  link.click();
                }}
              >
                <DownloadIcon className='mr-2 h-6 w-6' />
                {t('Download upscaled image')}
              </Button>
            </div>
          )}
        </div>

        <div className='md:col-span-4'>
          <div className='mx-auto h-screen'>
            {newBeforeImage && newAfterImage ? (
              <div className='relative relative flex items-center justify-center text-center'>
                {isFileLoading && (
                  <div className='absolute '>
                    <Spinner className='animate-spin-fast -ml-1 mr-3 h-16 text-primary-700' />
                  </div>
                )}
                <ImgComparisonSlider
                  className={clsxm(isFileLoading ? 'opacity-40' : '', 'w-full')}
                >
                  <figure slot='first' className='relative m-0'>
                    <img src={newBeforeImage} width='100%' />
                    <figcaption className='absolute left-3 top-1/2 -translate-y-1/2 transform bg-black/70 px-2 py-1 text-sm font-semibold leading-none text-white'>
                      Before
                    </figcaption>
                  </figure>
                  <figure slot='second' className='relative m-0'>
                    <img slot='second' src={newAfterImage} width='100%' />
                    <figcaption className='absolute right-3 top-1/2 -translate-y-1/2 transform bg-black/70 px-2 py-1 text-sm font-semibold leading-none text-white'>
                      After
                    </figcaption>
                  </figure>
                </ImgComparisonSlider>
              </div>
            ) : (
              <div className='relative flex items-center justify-center text-center'>
                {isFileLoading && (
                  <div className='absolute '>
                    <Spinner className='animate-spin-fast -ml-1 mr-3 h-16 text-primary-700' />
                  </div>
                )}
                <img
                  slot='first'
                  src={beforeImage as string}
                  className={clsxm(isFileLoading ? 'opacity-40' : '', 'w-full')}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
